import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountingCompleted, useDebounce } from '@saiens/shared/hooks';
import { formatDate } from '@saiens/shared/utils';
import type { AccountingCompletedItem } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ArrowLeft } from 'lucide-react';

export function CompletedListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [myOrders, setMyOrders] = useState(false);

  const { data, isLoading, error } = useAccountingCompleted({
    search: debouncedSearch,
    myOrders: myOrders || undefined,
    limit: 50,
  });

  const items = data?.items ?? [];

  return (
    <div className="px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate('/accounting')} className="text-gray-500">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">已完工未驗收</h1>
          <p className="text-sm text-gray-500">施工完成但尚未驗收開票</p>
        </div>
      </div>

      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋訂單編號、案名、客戶..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <div className="flex gap-2">
          <FilterChip label="我的案件" active={myOrders} onClick={() => setMyOrders((p) => !p)} />
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有資料</div>
        ) : (
          <div className="divide-y">
            {items.map((item) => (
              <CompletedCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {data && items.length > 0 && (
        <p className="mt-4 text-center text-sm text-gray-500">
          {data.total > items.length
            ? `顯示 ${items.length} / ${data.total} 筆`
            : `共 ${data.total} 筆`}
        </p>
      )}
    </div>
  );
}

function CompletedCard({ item }: { item: AccountingCompletedItem }) {
  return (
    <div className="p-4">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900">
          {item.project_name || item.name}
        </p>
        <p className="truncate text-sm text-gray-500">{item.partner_name || '---'}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
          <span>{item.name}</span>
          {item.engineer_date && <span>完工: {formatDate(item.engineer_date)}</span>}
          {item.user_name && <span>{item.user_name}</span>}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          NT${item.amount_total.toLocaleString()}
        </span>
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
            item.days_since_completion > 30
              ? 'bg-red-100 text-red-700'
              : item.days_since_completion > 14
                ? 'bg-orange-100 text-orange-700'
                : 'bg-gray-100 text-gray-600'
          }`}
        >
          完工 {item.days_since_completion} 天
        </span>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
